// import axios from 'axios';
// import { BASE_URL } from './BaseUrl';

// export const request = async (api, { method, query, params, body }, isAbsolute = false) => {
//   const url = `${BASE_URL}${isAbsolute ? api : api}${params ? '/' + params : ''}`;
//   try {
//     const response = await axios({
//       method,
//       url,
//       params: query,
//       data: body
//     });
//     return response.data;
//   } catch (err) {
//     console.log(err);
//   }
// };
// src/utils/request.js
// utils/request.js
// src/utils/request.js
// src/utils/request.js
import axios from "axios";
import { BASE_URL } from "./BaseUrl";

/**
 * Get posture API key:
 * - Prefer build-time REACT_APP_POSTURE_API_KEY (baked into bundle)
 * - Fallback to runtime-injected window.__REACT_APP_POSTURE_API_KEY (injected by entrypoint.sh)
 */
function getPostureKey() {
  try {
    // build-time env (becomes string or undefined)
    const buildKey = typeof process !== "undefined" ? process.env.REACT_APP_POSTURE_API_KEY : undefined;
    if (buildKey) return buildKey;
    // runtime global (injected into index.html at container start)
    if (typeof window !== "undefined" && window.__REACT_APP_POSTURE_API_KEY) {
      return window.__REACT_APP_POSTURE_API_KEY;
    }
  } catch (e) {
    // ignore
  }
  return null;
}

// In-memory cache for ETag revalidation across identical requests
const etagStore = new Map();

const getStoredEtag = (key) => {
  if (etagStore.has(key)) return etagStore.get(key);
  try {
    if (typeof window !== "undefined" && window.sessionStorage) {
      const raw = sessionStorage.getItem(`_etag_${key}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        etagStore.set(key, parsed);
        return parsed;
      }
    }
  } catch (e) {}
  return null;
};

const setStoredEtag = (key, val) => {
  etagStore.set(key, val);
  try {
    if (typeof window !== "undefined" && window.sessionStorage) {
      sessionStorage.setItem(`_etag_${key}`, JSON.stringify(val));
    }
  } catch (e) {}
};

export const clearEtagStore = () => {
  etagStore.clear();
  try {
    if (typeof window !== "undefined" && window.sessionStorage) {
      Object.keys(sessionStorage).forEach((k) => {
        if (k.startsWith("_etag_")) sessionStorage.removeItem(k);
      });
    }
  } catch (e) {}
};
export const getEtagStore = () => etagStore;

export const request = async (
  api,
  { method = "get", query = {}, params = "", body = null, headers: customHeaders = {}, signal, cache } = {},
  isAbsolute = false
) => {
  // Build URL (keeps your existing behavior of params => /:id)
  const url = isAbsolute ? api : `${BASE_URL}${api}${params ? `/${params}` : ""}`;

  // token from localStorage or sessionStorage (if any)
  const token = typeof window !== "undefined" ? (localStorage.getItem("token") || sessionStorage.getItem("token")) : null;

  // Build headers (caller-provided customHeaders should override defaults)
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  // If cache: 'no-store' was specified, ensure browser and proxies bypass disk/memory cache
  if (cache === "no-store" || customHeaders?.["Cache-Control"]?.includes("no-cache")) {
    headers["Cache-Control"] = "no-cache";
    headers["Pragma"] = "no-cache";
  }

  // merge custom headers provided by caller (e.g., x-api-key, x-encrypted)
  if (customHeaders && typeof customHeaders === "object") Object.assign(headers, customHeaders);

  // Add posture key if available and not already provided by caller
  const postureKey = getPostureKey();
  if (!headers["x-api-key"] && postureKey) {
    headers["x-api-key"] = postureKey;
  }

  // ETag revalidation for GET requests
  const isGet = String(method).toLowerCase() === "get";
  const etagKey = isGet ? `${url}?${new URLSearchParams(query || {}).toString()}` : null;
  const cachedEntry = isGet && etagKey ? getStoredEtag(etagKey) : null;
  if (isGet && etagKey && cachedEntry && !headers["If-None-Match"]) {
    headers["If-None-Match"] = cachedEntry.etag;
  }

  // Debug: log which headers names will be sent (do NOT log header values)
  try {
    if (typeof window !== "undefined" && window.console) {
      const headerNames = Object.keys(headers || {});
      console.debug("[request] URL:", url);
      console.debug("[request] method:", method);
      console.debug("[request] header names:", headerNames);
      console.debug("[request] postureKey present (boolean):", !!postureKey);
    }
  } catch (e) {
    /* ignore logging errors */
  }

  // axios config
  const axiosConfig = {
    method,
    url,
    headers,
    params: query,
    maxBodyLength: Infinity,
    signal,
    validateStatus: (status) => (status >= 200 && status < 300) || status === 304,
  };

  // If body is FormData (file upload), let axios/browser set the Content-Type + boundary.
  if (body instanceof FormData) {
    axiosConfig.data = body;
    // do NOT set Content-Type header
  } else if (body !== undefined && body !== null) {
    // normal JSON body
    axiosConfig.headers = { "Content-Type": "application/json", ...axiosConfig.headers };
    axiosConfig.data = typeof body === "string" ? body : JSON.stringify(body);
  }

  try {
    const response = await axios(axiosConfig);

    // If server returned 304 Not Modified, reuse previously cached payload
    if (response.status === 304 && etagKey) {
      const cached = getStoredEtag(etagKey);
      if (cached && cached.data) {
        return cached.data;
      }
      // If server returned 304 but local storage is missing, refetch cleanly without If-None-Match
      const freshHeaders = { ...headers };
      delete freshHeaders["If-None-Match"];
      freshHeaders["Cache-Control"] = "no-cache";
      const fallbackResponse = await axios({
        ...axiosConfig,
        headers: freshHeaders,
        validateStatus: (status) => status >= 200 && status < 300,
      });
      return fallbackResponse.data;
    }

    // If server returned 200 with an ETag header, store it for future revalidation
    if (response.status === 200 && isGet && etagKey) {
      const respEtag = response.headers?.etag || response.headers?.ETag;
      if (respEtag && response.data) {
        setStoredEtag(etagKey, { etag: respEtag, data: response.data });
      }
    }

    return response.data;
  } catch (err) {
    // Helpful debug logging
    if (err.response) {
      console.error("API request error:", err.response.status, err.response.data);
      throw err.response.data || err;
    } else {
      console.error("API request error:", err.message);
      throw err;
    }
  }
};

export default request;
